<script setup lang="ts">
import { computed } from "vue";
import LoadingSpinner from "../Loading/LoadingSpinner.vue";

const props = withDefaults(
  defineProps<{
    label: string;
    color:
      | "primary"
      | "button"
      | "destructive"
      | "super-cta"
      | "super-cta-primary"
      | "disabled"
      | "inline"
      | "inline-destructive";
    size?: "sm" | "md" | "lg";
    isLoading?: boolean;
    plus?: boolean;
    pulse?: boolean;
    disabled?: boolean;
  }>(),
  {
    size: "md",
  }
);

const focusClass = computed(() => {
  if (props.color === "destructive" || props.color === "inline-destructive") {
    return "focus-visible:outline focus-visible:outline-2 focus-visible:outline-app-red-500 focus-visible:outline-offset-2";
  }
  return "focus-ring";
});

const cursorClass = computed(() => (props.color === "disabled" ? "cursor-not-allowed" : "cursor-pointer"));

const sizeClass = computed(() => {
  // sm/md use the typography scale's own bold variant; no 16px bold token exists yet, so lg
  // falls back to font-bold directly (t-body has no t-body-bold counterpart in typography.css).
  if (props.size === "sm") return "min-h-[32px] py-1.5 px-3 t-caption-bold";
  if (props.size === "lg") return "min-h-[48px] py-3 px-6 t-body font-bold";
  return "min-h-[40px] py-2.5 px-5 t-small-bold"; // md (default)
});

const button = computed(() => {
  if (props.color === "super-cta") return "bg-app-green-700 dark:bg-app-green-fill rounded-full text-white hover:bg-app-green-800 dark:hover:bg-app-green-fill-hover";
  if (props.color === "button") return "bg-slate-200 rounded-8 text-type-default hover:bg-slate-300";
  if (props.color === "primary") return "bg-app-blue-600 dark:bg-app-blue-fill rounded-8 text-white hover:bg-app-blue-700 dark:hover:bg-app-blue-fill-hover";
  if (props.color === "super-cta-primary") return "bg-app-blue-600 dark:bg-app-blue-fill rounded-full text-white hover:bg-app-blue-700 dark:hover:bg-app-blue-fill-hover";
  if (props.color === "disabled") return "bg-slate-200 rounded-8 text-type-muted cursor-not-allowed";
  if (props.color === "destructive")
    return "rounded-8 border-2 border-app-red-700 text-app-red-700 hover:border-app-red-800 hover:text-app-red-800";
  if (props.color === "inline")
    return "rounded-8 border hover:bg-slate-200 border-slate-200 text-slate-600 hover:border-slate-300";
  if (props.color === "inline-destructive")
    return "rounded-4 border border-app-red-700 text-app-red-700 hover:border-app-red-800 hover:text-app-red-800";
  return "";
});
</script>

<template>
  <button
    class="interactive-active duration-fast ease-standard relative leading-none whitespace-nowrap transition-all disabled:cursor-not-allowed"
    :disabled="isLoading || disabled"
    :class="[
      button,
      sizeClass,
      focusClass,
      cursorClass,
      {
        'flex items-center justify-center gap-2': isLoading || plus,
      },
      { relative: pulse },
    ]">
    <div
      v-if="pulse"
      class="bg-app-blue-600 absolute -top-1 -right-1 z-20 flex h-1 w-1 animate-pulse items-center justify-center rounded-full p-2 text-[9px] leading-none text-white"></div>

    <font-awesome-icon
      v-if="plus"
      icon="fa-solid fa-plus"
      class="cursor-pointer text-xs" />
    <span :class="{ 'opacity-0': isLoading }">{{ label }}</span>
    <LoadingSpinner
      v-if="isLoading"
      class="absolute"
      size="xs"
      :fill="props.color" />
  </button>
</template>
